/**
 * The client half of the copy system.
 *
 * Every user-visible sentence on this site is authored once, in
 * `web/templates/base/macros/states.html`, and server-rendered into an inert
 * `<template>` by `partials/state_bank.html`. This module clones the one it
 * needs and fills the `data-slot` spans.
 *
 * There is no string of user copy in this file, and there must never be one.
 * If a state needs a word this module would have to supply, that word belongs
 * in the macro and the value belongs in a slot.
 */

import type { Notice, Problem } from './api-types.ts';
import { data, qs, qsa } from './dom.ts';
import { track } from './events.ts';
import { clock, minutesCost, minutesRemaining, resetsIn } from './format.ts';

export type SlotValues = Record<string, string | null | undefined>;

/**
 * Repeated lines for a `data-slot-list` element — the download row's loss
 * lines, where the COUNT is data and only the container is authored copy.
 *
 * Deliberately separate from `SlotValues`: a slot holds one string, and
 * overloading it with an array is how a list ends up joined with a comma in a
 * language that does not use commas that way.
 */
export type SlotLists = Record<string, readonly string[] | undefined>;

/**
 * Fill `data-slot` spans and drop `data-optional` fragments with no value.
 *
 * `data-optional-absent` is the other half: a fragment that stands in for an
 * optional one and goes when the value IS there. B6 needs it — "uses about
 * {4} of your {90} daily minutes" cannot simply lose its number, because "uses
 * your 90 daily minutes" says something false, so the macro carries "some of
 * your" beside it and exactly one of the two survives.
 */
function fill(root: ParentNode, values: SlotValues, lists: SlotLists = {}): void {
  for (const node of qsa<HTMLElement>('[data-slot]', root)) {
    const key = data(node, 'slot');
    if (!key) continue;
    const value = values[key];
    // textContent, never innerHTML: `title`, `uploader`, `host` and filenames
    // are all attacker-controlled strings from a third party.
    if (value !== undefined && value !== null) node.textContent = value;
  }
  for (const node of qsa<HTMLElement>('[data-slot-href]', root)) {
    const key = data(node, 'slot-href');
    if (!key) continue;
    const value = values[key];
    if (value) node.setAttribute('href', value);
  }
  for (const node of qsa<HTMLElement>('[data-optional]', root)) {
    const key = data(node, 'optional');
    if (!key) continue;
    const value = values[key];
    if (value === undefined || value === null || value === '') node.remove();
  }
  for (const node of qsa<HTMLElement>('[data-optional-absent]', root)) {
    const key = data(node, 'optional-absent');
    if (!key) continue;
    const value = values[key];
    if (value !== undefined && value !== null && value !== '') node.remove();
  }
  for (const node of qsa<HTMLElement>('[data-slot-list]', root)) {
    const key = data(node, 'slot-list');
    if (!key) continue;
    const items = lists[key];
    // An empty list removes the container rather than leaving an empty <ul>,
    // which reads to a screen reader as 'list, 0 items' and to everyone else
    // as a rendering bug.
    if (!items || items.length === 0) {
      node.remove();
      continue;
    }
    node.replaceChildren(
      ...items.map((text) => {
        const li = document.createElement('li');
        // textContent: a loss detail is a feature engine's string and can carry
        // whatever was in the user's file.
        li.textContent = text;
        return li;
      }),
    );
  }
}

/**
 * Clone a state by name. Returns null when the page did not include that
 * state in its bank — which is a bug, and is reported as one rather than
 * silently rendering nothing.
 */
export function renderState(
  name: string,
  values: SlotValues = {},
  lists: SlotLists = {},
): DocumentFragment | null {
  const tpl = qs<HTMLTemplateElement>(`template[data-state-template="${CSS.escape(name)}"]`);
  if (!tpl) {
    console.error(`[states] no template for "${name}" on this page`);
    return null;
  }
  const frag = tpl.content.cloneNode(true) as DocumentFragment;
  fill(frag, values, lists);
  return frag;
}

/** The fallback every state bank carries. See showState. */
export const GENERIC_STATE = 'GENERIC_ERROR';

function hasTemplate(name: string): boolean {
  return qs(`template[data-state-template="${CSS.escape(name)}"]`) !== null;
}

/**
 * A state's upload wording (`E5_UPLOAD`, `E6_UPLOAD`, `E12_UPLOAD`) when the
 * page carries one, else the state itself. The same state and the same quota
 * outcome, said about a file: "This video has no audio" and "the platform has
 * probably changed something" are not true of an upload. Only the pages that
 * handle uploads (the tool page, an upload's job page) bank all of these, and
 * a podcast's job page banks E5's and E6's (see `podcastVariant`).
 */
export function uploadVariant(
  name: string,
  exists: (name: string) => boolean = hasTemplate,
): string {
  const variant = `${name}_UPLOAD`;
  return exists(variant) ? variant : name;
}

/**
 * The value an upload's render carries. A line that does not fit a file the
 * visitor holds, such as E7's "For a long podcast, the episode's own show
 * notes…", is `data-optional-absent="upload"` in its macro, so fill() drops it
 * wherever this is set: the tool page (showProblem's `upload`) and an
 * upload's job page.
 */
export const UPLOAD_FLAG: Readonly<SlotValues> = { upload: 'y' };

/**
 * A state's podcast wording (`E12_PODCAST`) when the page carries one, else
 * its recording wording (`E5_UPLOAD`, `E6_UPLOAD`), else the state itself. An
 * episode is a recording, so "No speech in this file" fits it; E12's upload
 * advice (try the file again, save it as an MP3) does not, and neither does
 * the link wording's "if this video keeps failing". A podcast's job page
 * banks these, and only it calls this.
 */
export function podcastVariant(
  name: string,
  exists: (name: string) => boolean = hasTemplate,
): string {
  const variant = `${name}_PODCAST`;
  return exists(variant) ? variant : uploadVariant(name, exists);
}

/**
 * Which template a state renders with: its own, else GENERIC_ERROR, else none.
 * Pure, so the rule is testable without a DOM.
 */
export function templateFor(name: string, exists: (name: string) => boolean): string | null {
  if (exists(name)) return name;
  return exists(GENERIC_STATE) ? GENERIC_STATE : null;
}

/**
 * Put a state into a live region and move focus to it.
 *
 * Both, not either: `aria-live` announces it for a screen reader that is
 * already elsewhere on the page, and the focus move is what makes the error
 * findable for a keyboard user who just pressed a button and is now sitting on
 * a control whose result is 300px away.
 */
export function showState(region: HTMLElement, name: string, values: SlotValues = {}): boolean {
  // The template is found BEFORE the region is touched. This used to empty the
  // region first and then learn there was no template, so an upload refused
  // as UNSUPPORTED_MEDIA on a tool page replaced the B6 banner with nothing at
  // all (live, 2026-10-01). A missing template is still a bug and the console
  // still says which; the visitor gets GENERIC_ERROR, which every bank
  // carries. A page with no bank at all keeps what it was showing.
  const chosen = templateFor(name, hasTemplate);
  if (chosen !== name) console.error(`[states] no template for "${name}" on this page`);
  if (chosen === null) return false;
  const frag = renderState(chosen, values);
  if (!frag) return false;
  region.replaceChildren(frag);
  region.focus({ preventScroll: false });
  return true;
}

export function clearState(region: HTMLElement): void {
  region.replaceChildren();
}

/* ── mapping the wire onto a state name ───────────────────────────────── */

/**
 * Codes that carry no `state` because docs/USER_JOURNEY.md has no state for
 * them. All three are transport failures and share the one DRAFT banner.
 */
const TRANSPORT_CODES: ReadonlySet<string> = new Set([
  'NETWORK',
  'TIMEOUT',
  'BAD_RESPONSE',
  'MINT_RATE_LIMITED',
]);

export interface ResolvedState {
  readonly name: string;
  readonly values: SlotValues;
}

/**
 * Turn a problem into a state name plus its slot values.
 *
 * Switches on `state`, then on `code` — never on `title` or `detail`, which
 * are developer diagnostics that may change without notice and are not
 * translated, not approved, and not for users.
 *
 * `extra` is for values the response cannot carry: the platform LABEL, for
 * instance, which the client already knows from the badge it drew at paste
 * time and which the server sends only as a machine name.
 */
export function problemToState(problem: Problem, extra: SlotValues = {}): ResolvedState {
  const values: SlotValues = {};

  if (problem.url) {
    values['host'] = problem.url.host;
    if (problem.url.podcast_app) values['app'] = problem.url.podcast_app;
  }

  if (problem.duration) {
    values['duration'] = clock(problem.duration.duration_s);
    values['limit'] = String(Math.round(problem.duration.limit_s / 60));
  }

  if (problem.quota) {
    const q = problem.quota;
    values['minutes_left'] = String(minutesRemaining(q.asr_seconds_remaining));
    values['budget_minutes'] = String(Math.round(q.asr_seconds_budget / 60));
    if (q.asr_seconds_needed !== null) {
      values['duration'] = clock(q.asr_seconds_needed);
      // E8's "This runs {41:20} and you have {12} minutes left" needs both
      // numbers; without this flag it keeps its number-free wording, which is
      // what the job page (whose poll carries neither) always shows.
      values['shortfall'] = 'y';
    }
    const reset = resetsIn(q.resets_in_s);
    if (reset) {
      values['resets'] = 'y';
      values['resets_h'] = reset.h;
      values['resets_m'] = reset.m;
    }
  }

  if (problem.running_job) {
    values['job_url'] = `/t/${problem.running_job.job_id}`;
    if (problem.running_job.eta_s !== null) {
      values['eta'] = 'y';
      Object.assign(values, minuteCount(minutesCost(problem.running_job.eta_s)));
    }
  }

  if (problem.platform) {
    values['platform'] = problem.platform.platform;
    values['platform_2'] = problem.platform.platform;
  }

  if (problem.code === GEMINI_LIMIT) {
    const limit = geminiLimitState(limitFromProblem(problem));
    return { name: limit.name, values: { ...limit.values, ...extra } };
  }
  // A summary or captions asked of a transcript with no speech in it (422 NO_SPEECH). The
  // server files it under E6, whose copy is about a video's audio and its minutes; nothing
  // was transcribed here, so it has its own words.
  if (problem.code === 'NO_SPEECH') return { name: 'NO_SPEECH', values: { ...extra } };

  const merged = { ...values, ...extra };
  const name = problem.state ?? (TRANSPORT_CODES.has(problem.code) ? 'NETWORK' : problem.code);
  if (name === 'E1') return { name: e1Variant(problem, merged), values: merged };
  if (name === 'E3') return { name: e3Variant(problem), values: merged };
  if (name === 'E20') return { name: e20Variant(problem), values: merged };
  return { name, values: merged };
}

/**
 * E20 has two causes. Platform health (an extractor broke and the platform is
 * switched off) carries only the `platform` extension, and its copy says "a
 * day or two". A podcast feed or audio file that timed out, dropped the
 * connection or answered 5xx at probe time also carries `url`, with
 * `platform: "podcast"`, and is one slow host: its copy says to retry in a
 * minute. The `url` member is the discriminator, so a podcast platform
 * switched off by health keeps the plain E20.
 */
function e20Variant(problem: Problem): string {
  return problem.url?.platform === 'podcast' ? 'E20_PODCAST' : 'E20';
}

/**
 * E3 for a podcast episode the probe found gone or refused (a 403, 404 or 410,
 * a web page where the file should be) says "This episode isn't available":
 * E3's own copy blames a video, a post with no video in it, or a region. The
 * server adds `url`, with `platform: "podcast"`, to a podcast's E3 alone, and
 * the choice reads it exactly as `e20Variant` does.
 *
 * A Spotify episode link has two refusals of its own, named by `url.reason`:
 * a show with no public feed (a Spotify exclusive) and an episode its feed
 * does not carry. An unknown reason gets the podcast wording.
 */
function e3Variant(problem: Problem): string {
  if (problem.url?.platform !== 'podcast') return 'E3';
  if (problem.url.reason === 'spotify_exclusive') return 'E3_SPOTIFY_EXCLUSIVE';
  if (problem.url.reason === 'not_in_feed') return 'E3_NOT_IN_FEED';
  return 'E3_PODCAST';
}

/**
 * A hostname a person could have meant: labels separated by dots, no spaces.
 * "not a link at all" parses, after the page adds https://, to a host with
 * spaces in it, and "hello" to one with no dot. landing.ts refuses both before
 * sending, and an E1 that comes back with such a host gets the same answer.
 */
export function plausibleHost(host: string | null | undefined): boolean {
  return typeof host === 'string' && /^[^\s.]+(?:\.[^\s.]+)+\.?$/.test(host);
}

/**
 * Which of E1's templates fits, from the problem's `url` extension.
 *
 * The plain E1 names the host and lists the platforms that work, which
 * contradicts itself when the host is one of them, so a launch platform's host
 * gets the variant that says the link is not one video. That variant needs the
 * platform's noun ("X video"), which only GET /api/platforms carries and the
 * caller passes in `noun`; without it the plain E1 is what is left, because
 * "a single twitter" would be worse.
 *
 * Before that: a shortlink our servers cannot follow (fb.watch, `shortlink:
 * true`) says to open it in the browser, whatever its platform; and `podcast`
 * is the one platform whose E1 is not about videos, sent for an Apple Podcasts
 * show page, whose episodes work. Neither needs a noun. After it: a host no
 * one could have meant gets "That doesn't look like a link" rather than "We
 * can't transcribe links from not a link at all".
 */
function e1Variant(problem: Problem, values: SlotValues): string {
  if (problem.url?.podcast_app) return 'E1_PODCAST_APP';
  if (problem.url?.shortlink === true) return 'E1_SHORTLINK';
  if (problem.url?.platform === 'podcast') return 'E1_PODCAST_SHOW';
  if (problem.url?.platform && values['noun']) return 'E1_PLATFORM';
  if (!plausibleHost(problem.url?.host)) return 'E1_NOT_A_LINK';
  return 'E1';
}

/**
 * A count of minutes for a slot, and whether it is one. E11, E21 and A3 say
 * "a minute" for one and "{n} minutes" otherwise; the words are in the
 * templates, and `one_minute` picks between them.
 */
export function minuteCount(minutes: number): SlotValues {
  return { minutes: String(minutes), one_minute: minutes === 1 ? 'y' : null };
}

/* ── the language model unavailable (SEO_BRIEF_09 2.2) ───────────────── */

/** The problem code of a refusal for the language model's limit (contracts/api.md 1.19.0). */
export const GEMINI_LIMIT = 'GEMINI_LIMIT';

/**
 * What a GEMINI_LIMIT refusal says, as lib/gemini.ts hands it over (`GeminiLimit`): whose limit
 * (`global` for everyone's day, `visitor` for this visitor's cap), when it resets (epoch seconds,
 * the next midnight Pacific), the cap, and the minutes speech recognition would spend instead
 * when a "Translate to English" can still be done that way.
 */
export interface GeminiLimitShape {
  readonly scope: string;
  readonly resetsAt: number;
  readonly cap: number;
  readonly fallback: null | { readonly minutes: number };
}

/**
 * Seconds from `now` (epoch ms) to the next midnight in Los Angeles, from the zone's own clock.
 * Also the live translator's, for a Hold to talk clip the language model had no room for
 * (lib/live-translator/limit.ts).
 */
export function untilPacificMidnight(now: number): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      hourCycle: 'h23',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(new Date(now));
    const part = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? NaN);
    const elapsed = part('hour') * 3600 + part('minute') * 60 + part('second');
    return Number.isFinite(elapsed) ? 86_400 - elapsed : null;
  } catch {
    return null;
  }
}

/**
 * The visitor's local clock time of an epoch second, hours and minutes in the browser's own
 * convention ("12:00 AM", "09:00"). A reset the server did not send, or sent as nonsense, is the
 * next midnight Pacific worked out here, which is what it always is (brief 2.2).
 */
export function resetClock(epochS: number, locale?: string, now: number = Date.now()): string {
  let at = epochS;
  if (!Number.isFinite(at) || at * 1000 <= now) {
    const left = untilPacificMidnight(now);
    at = now / 1000 + (left ?? 0);
  }
  try {
    return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(
      new Date(at * 1000),
    );
  } catch {
    return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(
      new Date(at * 1000),
    );
  }
}

/**
 * The limit's template and slots: GEMINI_LIMIT_VISITOR for this visitor's cap, otherwise
 * GEMINI_LIMIT_GLOBAL (the two say the same words: no visitor is told of a limit, the founder's
 * ruling of 2026-10-26); `reset` in local time; and the speech-recognition offer's minutes,
 * whole and rounded up, with `one_minute` for one. No fallback, no offer: the template's
 * `data-optional="fallback_minutes"` goes.
 */
export function geminiLimitState(limit: GeminiLimitShape, locale?: string): ResolvedState {
  const values: SlotValues = { reset: resetClock(limit.resetsAt, locale) };
  const minutes = limit.fallback?.minutes;
  if (typeof minutes === 'number' && Number.isFinite(minutes) && minutes > 0) {
    const whole = Math.ceil(minutes);
    values['fallback_minutes'] = String(whole);
    values['one_minute'] = whole === 1 ? 'y' : null;
  }
  const name = limit.scope === 'visitor' ? 'GEMINI_LIMIT_VISITOR' : 'GEMINI_LIMIT_GLOBAL';
  return { name, values };
}

/** A GEMINI_LIMIT problem's flat extension members (`scope`, `resets_at`, `cap`, `fallback`). */
function limitFromProblem(problem: Problem): GeminiLimitShape {
  const ext = problem as unknown as Readonly<Record<string, unknown>>;
  const fallback = ext['fallback'];
  const minutes =
    typeof fallback === 'object' && fallback !== null
      ? (fallback as Readonly<Record<string, unknown>>)['minutes']
      : undefined;
  return {
    scope: typeof ext['scope'] === 'string' ? ext['scope'] : 'global',
    resetsAt: typeof ext['resets_at'] === 'number' ? ext['resets_at'] : Number.NaN,
    cap: typeof ext['cap'] === 'number' ? ext['cap'] : 0,
    fallback: typeof minutes === 'number' ? { minutes } : null,
  };
}

/** Notices ride along with successful responses; same slot machinery. */
export function noticeToState(notice: Notice): ResolvedState {
  const values: SlotValues = {};
  if (typeof notice.asr_seconds_estimate === 'number') {
    Object.assign(values, minuteCount(minutesCost(notice.asr_seconds_estimate)));
  }
  return { name: notice.state ?? notice.code, values };
}

/**
 * Render a problem and record it.
 *
 * The `error_state` counter is fired from here rather than from each call
 * site, so per-state frequency cannot drift from the copy that was actually
 * shown — which is the whole reason `state` is on the wire.
 */
export function showProblem(
  region: HTMLElement,
  problem: Problem,
  extra: SlotValues = {},
  options: { readonly upload?: boolean } = {},
): ResolvedState {
  const resolved = problemToState(problem, extra);
  const shown = options.upload ? uploadVariant(resolved.name) : resolved.name;
  showState(
    region,
    shown,
    options.upload ? { ...resolved.values, ...UPLOAD_FLAG } : resolved.values,
  );
  // The wire's state, not the wording: per-state frequency counts E5 once.
  track('error_state', { state: resolved.name });
  return resolved;
}

/** Append a notice under an existing region without displacing what is there. */
export function appendNotice(region: HTMLElement, notice: Notice): void {
  const resolved = noticeToState(notice);
  const frag = renderState(resolved.name, resolved.values);
  if (frag) region.append(frag);
}
